import Phaser from 'phaser';

/**
 * Shared gold-bordered HUD tooltip. Matches the Divine Wrath / Zone Cleared popup palette:
 * dark-brown fill + gold outer border + dim-gold inner frame + bronze text. Returns a
 * Container so callers can .destroy() it like a single object.
 *
 * Usage mirrors `scene.add.text(x, y, text, style).setOrigin(ox, oy)`:
 *   tooltip = createHudTooltip(this, x, y, text, 0.5, 1, { fontSize: '10px' });
 */
export function createHudTooltip(
  scene: Phaser.Scene,
  x: number, y: number, text: string,
  originX = 0, originY = 0,
  opts: {
    color?: string;
    fontSize?: string;
    wrap?: number;
    depth?: number;
    /** 'center' aligns multi-line text inside a fixed-width box — used by
     *  item tooltips (HeroScene / InventoryScene / merchant) so the stat
     *  lines sit centered the way the mockup shows. Default 'left'. */
    align?: 'left' | 'center';
    /** Optional bold accent line rendered above `text` in its own colour.
     *  Used for per-tooltip status lines that need to stand out against the
     *  rarity-tinted body (e.g. red "Can't equip" / green "Can equip on …"). */
    accent?: { text: string; color: string };
  } = {},
): Phaser.GameObjects.Container {
  const color = opts.color ?? '#c8973a';
  const fontSize = opts.fontSize ?? '11px';
  const wrap = opts.wrap ?? 240;
  const depth = opts.depth ?? 55;
  const align = opts.align ?? 'left';
  const pad = 8;
  // Centered tooltips use a fixedWidth equal to `wrap` so each line centers
  // inside the same box; left-aligned keeps the natural-width behaviour.
  const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
    fontSize, fontFamily: 'Georgia, serif', color,
    lineSpacing: 4, wordWrap: { width: wrap }, align,
  };
  if (align === 'center') textStyle.fixedWidth = wrap;
  let accentObj: Phaser.GameObjects.Text | null = null;
  if (opts.accent && opts.accent.text) {
    accentObj = scene.add.text(pad, pad, opts.accent.text, {
      fontSize, fontFamily: 'Georgia, serif', color: opts.accent.color,
      fontStyle: 'bold', lineSpacing: 4, wordWrap: { width: wrap }, align,
      ...(align === 'center' ? { fixedWidth: wrap } : {}),
    }).setResolution(2);
  }
  const accentH = accentObj ? Math.ceil(accentObj.height + 6) : 0;
  const textObj = scene.add.text(pad, pad + accentH, text, textStyle).setResolution(2);
  const w = Math.ceil(Math.max(textObj.width, accentObj?.width ?? 0) + pad * 2);
  const h = Math.ceil(textObj.height + accentH + pad * 2);
  const rect = scene.add.rectangle(0, 0, w, h, 0x0d0a08, 0.95)
    .setStrokeStyle(1, 0xc8973a, 0.95).setOrigin(0, 0);
  const innerBorder = scene.add.rectangle(0, 0, w - 4, h - 4, 0x0d0a08, 0)
    .setStrokeStyle(1, 0x5a3600, 0.5).setOrigin(0, 0).setPosition(2, 2);
  const children: Phaser.GameObjects.GameObject[] = [rect, innerBorder, textObj];
  if (accentObj) children.push(accentObj);
  const container = scene.add.container(
    x - originX * w,
    y - originY * h,
    children,
  ).setDepth(depth);
  container.setSize(w, h);
  return container;
}

/**
 * Structured item tooltip — separate text objects per section with divider
 * lines between, so each part can carry its own colour. The earlier single-
 * `createHudTooltip` approach tinted the whole body one colour (rarity), which
 * made uncommon items look entirely green including their stats.
 *
 * Layout (top → bottom):
 *   1. Name line          — rarity colour, bold
 *   ───────────────────── (gold hairline divider)
 *   2. Stat lines         — bronze neutral
 *   ───────────────────── (divider)
 *   3. Bottom lines       — bronze dim (sell price, [double-click to unequip])
 *   4. Class-equip line   — accent colour + weapon/armor type appended with " · "
 */
export function createItemTooltip(
  scene: Phaser.Scene,
  x: number, y: number,
  spec: {
    name: string;
    nameColor: string;
    statLines: string[];
    bottomLines: string[];
    classLine?: { text: string; color: string };
    typeLabel?: string;           // appended to classLine with " · "
    fontSize?: string;
    wrap?: number;
    depth?: number;
  },
  originX = 0, originY = 0,
): Phaser.GameObjects.Container {
  const fontSize = spec.fontSize ?? '11px';
  const wrap = spec.wrap ?? 240;
  const depth = spec.depth ?? 55;
  const pad = 8;
  const innerW = wrap;

  const STAT_COLOR = '#c8973a';   // bronze baseline
  const BOTTOM_COLOR = '#a07840'; // dim bronze for sell / unequip hint
  const DIVIDER_COLOR = 0x5a3600;

  const children: Phaser.GameObjects.GameObject[] = [];
  const textCommon: Partial<Phaser.Types.GameObjects.Text.TextStyle> = {
    fontSize, fontFamily: 'Georgia, serif',
    lineSpacing: 4, wordWrap: { width: innerW }, align: 'center',
    fixedWidth: innerW,
  };

  let cursorY = pad;

  // 1. Name (rarity colour, bold)
  const nameTxt = scene.add.text(pad, cursorY, spec.name, {
    ...textCommon, color: spec.nameColor, fontStyle: 'bold',
  }).setResolution(2);
  children.push(nameTxt);
  cursorY += Math.ceil(nameTxt.height) + 6;

  // Divider between name and stats
  if (spec.statLines.length > 0) {
    const div1 = scene.add.rectangle(pad + 12, cursorY, innerW - 24, 1, DIVIDER_COLOR, 0.65).setOrigin(0, 0);
    children.push(div1);
    cursorY += 7;

    const statsTxt = scene.add.text(pad, cursorY, spec.statLines.join('\n'), {
      ...textCommon, color: STAT_COLOR,
    }).setResolution(2);
    children.push(statsTxt);
    cursorY += Math.ceil(statsTxt.height) + 6;
  }

  // Divider between stats and bottom
  const hasBottom = spec.bottomLines.length > 0 || spec.classLine;
  if (hasBottom) {
    const div2 = scene.add.rectangle(pad + 12, cursorY, innerW - 24, 1, DIVIDER_COLOR, 0.65).setOrigin(0, 0);
    children.push(div2);
    cursorY += 7;

    if (spec.bottomLines.length > 0) {
      const bottomTxt = scene.add.text(pad, cursorY, spec.bottomLines.join('\n'), {
        ...textCommon, color: BOTTOM_COLOR,
      }).setResolution(2);
      children.push(bottomTxt);
      cursorY += Math.ceil(bottomTxt.height) + 4;
    }

    if (spec.classLine) {
      const combined = spec.typeLabel
        ? `${spec.classLine.text}  ·  ${spec.typeLabel}`
        : spec.classLine.text;
      const classTxt = scene.add.text(pad, cursorY, combined, {
        ...textCommon, color: spec.classLine.color, fontStyle: 'bold',
      }).setResolution(2);
      children.push(classTxt);
      cursorY += Math.ceil(classTxt.height) + 2;
    }
  }

  const totalW = innerW + pad * 2;
  const totalH = cursorY + pad;

  // Background + inner border drawn last so they end up behind everything via
  // the container child order.
  const bg = scene.add.rectangle(0, 0, totalW, totalH, 0x0d0a08, 0.95)
    .setStrokeStyle(1, 0xc8973a, 0.95).setOrigin(0, 0);
  const innerBorder = scene.add.rectangle(2, 2, totalW - 4, totalH - 4, 0x0d0a08, 0)
    .setStrokeStyle(1, 0x5a3600, 0.5).setOrigin(0, 0);

  const container = scene.add.container(
    x - originX * totalW,
    y - originY * totalH,
    [bg, innerBorder, ...children],
  ).setDepth(depth);
  container.setSize(totalW, totalH);
  return container;
}
